import { useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { OUTREACH_CALL_TAB_SPEC, toggleOverride } from './dev-feature-toggles';

/**
 * Client read of the Outreach Calls tab — `symphonyOutreachCallTab`.
 *
 * Variant (`true`): the Outreach screen draws a third tab, Calls (`CallsBody`). Control (`false`): Contacts and
 * Inbox, byte for byte.
 *
 * It FAILS CLOSED to CONTROL, like the sibling gates, and like `alfred-in-outreach-flag` a FAILED read is not
 * cached: control this time, asked again on the next mount. A real answer stays cached.
 *
 * A dev-tools **Feature toggles** override wins over the conducted value, and is inert without a dev-tools grant.
 */

/** Matches the sibling gates' bound — a gate that has not answered in time IS control. */
const GATE_TIMEOUT_MS = 1500;

let cached: Promise<boolean> | null = null;

export function outreachCallTabEnabled(): Promise<boolean> {
  // Checked on every call, not memoized: the override can be set mid-session from the dev panel.
  const forced = toggleOverride(OUTREACH_CALL_TAB_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cached) return cached;
  cached = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/outreach-call-tab`, {
        credentials: 'include',
        signal: AbortSignal.timeout(GATE_TIMEOUT_MS),
      });
      if (!res.ok) {
        cached = null;
        return false;
      }
      return ((await res.json()) as boolean) === true;
    } catch {
      // Includes the abort: a gate that has not answered in time IS control — this time. Not latched.
      cached = null;
      return false;
    }
  })();
  return cached;
}

/**
 * The arm, for rendering: `undefined` until the gate answers, then the answer. A caller draws the tab only on
 * `true`, so a user outside the release never sees a tab flash in — and can tell "not answered yet" from "off",
 * so a `?tab=calls` restored from the URL is not thrown away before the gate has spoken.
 */
export function useOutreachCallTabEnabled(): boolean | undefined {
  const [enabled, setEnabled] = useState<boolean | undefined>(undefined);
  useEffect(() => {
    let live = true;
    void outreachCallTabEnabled().then((on) => {
      if (live) setEnabled(on);
    });
    return () => {
      live = false;
    };
  }, []);
  return enabled;
}
